

// In src/domains/agents/lib/prompt-variables.ts

import { z } from 'zod';

/**
 * @fileoverview This file centralizes the definitions of prompt variables available to AI agents.
 * Each entry includes a Zod schema for validation and metadata for UI display.
 * This ensures consistency and makes agent capabilities discoverable.
 */

// This type defines the structure for an agent's prompt variable.
// It's used to dynamically generate UI elements and tooltips.
export type AgentPromptVariable = {
    name: string;
    description: string;
};

// NOTE: To break circular dependencies, the schemas here are simplified to `z.any()`.
// The actual, specific schemas are used in the Genkit flows for validation.
// The `buildAgentContext` function dynamically populates these fields, so this change is safe.

export const AGENT_SCHEMA_OBJECTS = {

  // =============================================================================
  // COLLECTIONS (Full Entity Arrays)
  // =============================================================================
  
  characters: {
    key: 'characters',
    label: 'Characters (Full Initial Context)',
    schema: z.object({ characters: z.array(z.any()).optional() }),
    description: 'Full character details including personality, physical description, and summary',
    domain: 'characters',
  },

  charactersCurrent: {
    key: 'charactersCurrent',
    label: 'Characters (Full Current Context)',
    schema: z.object({ charactersCurrent: z.array(z.any()).optional() }),
    description: 'Full character details including personality, physical description, and summary',
    domain: 'characters',
  },

  characterRoles: {
    key: 'basicCharacters',
    label: 'Characters (Basic)',
    schema: z.object({ characterRoles: z.array(z.any()).optional() }),
    description: 'Role-related character information (name, category, plot role, planned arc)',
    domain: 'characters',
  },

  knownCharacters: {
    key: 'knownCharacters',
    label: 'Known Characters',
    schema: z.object({ knownCharacters: z.array(z.any()).optional() }),
    description: 'A list of all characters explicitly defined in the project',
    domain: 'characters',
  },

  worldElements: {
    key: 'worldElements',
    label: 'World Elements',
    schema: z.object({ worldElements: z.array(z.any()).optional() }),
    description: 'World-building elements (locations, cultures, systems, history)',
    domain: 'world-building',
  },

  knownWorldElements: {
    key: 'knownWorldElements',
    label: 'Known World Elements',
    schema: z.object({ knownWorldElements: z.array(z.any()).optional() }),
    description: 'A lightweight list of all world elements defined in the project (name, type, id)',
    domain: 'world-building',
  },

  // =============================================================================
  // WORLD OBJECTS (V3)
  // =============================================================================

  worldObjects: {
    key: 'worldObjects',
    label: 'World Objects (V3)',
    schema: z.object({ worldObjects: z.array(z.any()).optional() }),
    description: 'V3 world objects (artifacts, items) with latest snapshot resolved',
    domain: 'world-building',
  },

  worldObjectsCurrent: {
    key: 'worldObjectsCurrent',
    label: 'World Objects Current (V3)',
    schema: z.object({ worldObjectsCurrent: z.array(z.any()).optional() }),
    description: 'V3 world objects with latest snapshot fields flattened to top level',
    domain: 'world-building',
  },

  activeWorldObject: {
    key: 'activeWorldObject',
    label: 'Active World Object (V3)',
    schema: z.object({ activeWorldObject: z.any().optional() }),
    description: 'The currently active V3 world object with latest snapshot resolved',
    domain: 'world-building',
  },

  activeWorldObjectCurrent: {
    key: 'activeWorldObjectCurrent',
    label: 'Active World Object Current (V3)',
    schema: z.object({ activeWorldObjectCurrent: z.any().optional() }),
    description: 'The currently active V3 world object with latest snapshot fields flattened',
    domain: 'world-building',
  },

  worldObjectFieldDefinitions: {
    key: 'worldObjectFieldDefinitions',
    label: 'World Object Field Definitions',
    schema: z.object({ worldObjectFieldDefinitions: z.array(z.any()).optional() }),
    description: 'Field definitions for world objects, used for prompt injection',
    domain: 'world-building',
  },

  // =============================================================================
  // WORLD PLACES (V3)
  // =============================================================================

  worldPlaces: {
    key: 'worldPlaces',
    label: 'World Places (V3)',
    schema: z.object({ worldPlaces: z.array(z.any()).optional() }),
    description: 'V3 world places (locations, buildings) with latest snapshot resolved',
    domain: 'world-building',
  },

  worldPlacesCurrent: {
    key: 'worldPlacesCurrent',
    label: 'World Places Current (V3)',
    schema: z.object({ worldPlacesCurrent: z.array(z.any()).optional() }),
    description: 'V3 world places with latest snapshot fields flattened to top level',
    domain: 'world-building',
  },

  activeWorldPlace: {
    key: 'activeWorldPlace',
    label: 'Active World Place (V3)',
    schema: z.object({ activeWorldPlace: z.any().optional() }),
    description: 'The currently active V3 world place with latest snapshot resolved',
    domain: 'world-building',
  },

  activeWorldPlaceCurrent: {
    key: 'activeWorldPlaceCurrent',
    label: 'Active World Place Current (V3)',
    schema: z.object({ activeWorldPlaceCurrent: z.any().optional() }),
    description: 'The currently active V3 world place with latest snapshot fields flattened',
    domain: 'world-building',
  },

  worldPlaceFieldDefinitions: {
    key: 'worldPlaceFieldDefinitions',
    label: 'World Place Field Definitions',
    schema: z.object({ worldPlaceFieldDefinitions: z.array(z.any()).optional() }),
    description: 'Field definitions for world places, used for prompt injection',
    domain: 'world-building',
  },

  // =============================================================================
  // WORLD SETS (V3)
  // =============================================================================

  worldSets: {
    key: 'worldSets',
    label: 'World Sets (V3)',
    schema: z.object({ worldSets: z.array(z.any()).optional() }),
    description: 'V3 world sets (staged environments) with latest snapshot resolved',
    domain: 'world-building',
  },

  worldSetsCurrent: {
    key: 'worldSetsCurrent',
    label: 'World Sets Current (V3)',
    schema: z.object({ worldSetsCurrent: z.array(z.any()).optional() }),
    description: 'V3 world sets with latest snapshot fields flattened to top level',
    domain: 'world-building',
  },

  activeWorldSet: {
    key: 'activeWorldSet',
    label: 'Active World Set (V3)',
    schema: z.object({ activeWorldSet: z.any().optional() }),
    description: 'The currently active V3 world set with latest snapshot resolved',
    domain: 'world-building',
  },

  activeWorldSetCurrent: {
    key: 'activeWorldSetCurrent',
    label: 'Active World Set Current (V3)',
    schema: z.object({ activeWorldSetCurrent: z.any().optional() }),
    description: 'The currently active V3 world set with latest snapshot fields flattened',
    domain: 'world-building',
  },

  worldSetFieldDefinitions: {
    key: 'worldSetFieldDefinitions',
    label: 'World Set Field Definitions',
    schema: z.object({ worldSetFieldDefinitions: z.array(z.any()).optional() }),
    description: 'Field definitions for world sets, used for prompt injection',
    domain: 'world-building',
  },

  // =============================================================================
  // WORLD EVENTS (V3)
  // =============================================================================

  worldEvents: {
    key: 'worldEvents',
    label: 'World Events (V3)',
    schema: z.object({ worldEvents: z.array(z.any()).optional() }),
    description: 'V3 world events (historical events, prophecies, legends) with latest snapshot resolved',
    domain: 'world-building',
  },

  worldEventsCurrent: {
    key: 'worldEventsCurrent',
    label: 'World Events Current (V3)',
    schema: z.object({ worldEventsCurrent: z.array(z.any()).optional() }),
    description: 'V3 world events with latest snapshot fields flattened to top level',
    domain: 'world-building',
  },

  activeWorldEvent: {
    key: 'activeWorldEvent',
    label: 'Active World Event (V3)',
    schema: z.object({ activeWorldEvent: z.any().optional() }),
    description: 'The currently active V3 world event with latest snapshot resolved',
    domain: 'world-building',
  },

  activeWorldEventCurrent: {
    key: 'activeWorldEventCurrent',
    label: 'Active World Event Current (V3)',
    schema: z.object({ activeWorldEventCurrent: z.any().optional() }),
    description: 'The currently active V3 world event with latest snapshot fields flattened',
    domain: 'world-building',
  },

  worldEventFieldDefinitions: {
    key: 'worldEventFieldDefinitions',
    label: 'World Event Field Definitions',
    schema: z.object({ worldEventFieldDefinitions: z.array(z.any()).optional() }),
    description: 'Field definitions for world events, used for prompt injection',
    domain: 'world-building',
  },

  // =============================================================================
  // WORLD ORGANIZATIONS (V3)
  // =============================================================================

  worldOrganizations: {
    key: 'worldOrganizations',
    label: 'World Organizations (V3)',
    schema: z.object({ worldOrganizations: z.array(z.any()).optional() }),
    description: 'V3 world organizations (factions, nations, institutions) with latest snapshot resolved',
    domain: 'world-building',
  },

  worldOrganizationsCurrent: {
    key: 'worldOrganizationsCurrent',
    label: 'World Organizations Current (V3)',
    schema: z.object({ worldOrganizationsCurrent: z.array(z.any()).optional() }),
    description: 'V3 world organizations with latest snapshot fields flattened to top level',
    domain: 'world-building',
  },

  activeWorldOrganization: {
    key: 'activeWorldOrganization',
    label: 'Active World Organization (V3)',
    schema: z.object({ activeWorldOrganization: z.any().optional() }),
    description: 'The currently active V3 world organization with latest snapshot resolved',
    domain: 'world-building',
  },

  activeWorldOrganizationCurrent: {
    key: 'activeWorldOrganizationCurrent',
    label: 'Active World Organization Current (V3)',
    schema: z.object({ activeWorldOrganizationCurrent: z.any().optional() }),
    description: 'The currently active V3 world organization with latest snapshot fields flattened',
    domain: 'world-building',
  },

  worldOrganizationFieldDefinitions: {
    key: 'worldOrganizationFieldDefinitions',
    label: 'World Organization Field Definitions',
    schema: z.object({ worldOrganizationFieldDefinitions: z.array(z.any()).optional() }),
    description: 'Field definitions for world organizations, used for prompt injection',
    domain: 'world-building',
  },

  // =============================================================================
  // WORLD SPECIES (V3)
  // =============================================================================

  worldSpecies: {
    key: 'worldSpecies',
    label: 'World Species (V3)',
    schema: z.object({ worldSpecies: z.array(z.any()).optional() }),
    description: 'V3 world species (creatures, races, flora) with latest snapshot resolved',
    domain: 'world-building',
  },

  worldSpeciesCurrent: {
    key: 'worldSpeciesCurrent',
    label: 'World Species Current (V3)',
    schema: z.object({ worldSpeciesCurrent: z.array(z.any()).optional() }),
    description: 'V3 world species with latest snapshot fields flattened to top level',
    domain: 'world-building',
  },

  activeWorldSpecies: {
    key: 'activeWorldSpecies',
    label: 'Active World Species (V3)',
    schema: z.object({ activeWorldSpecies: z.any().optional() }),
    description: 'The currently active V3 world species with latest snapshot resolved',
    domain: 'world-building',
  },

  activeWorldSpeciesCurrent: {
    key: 'activeWorldSpeciesCurrent',
    label: 'Active World Species Current (V3)',
    schema: z.object({ activeWorldSpeciesCurrent: z.any().optional() }),
    description: 'The currently active V3 world species with latest snapshot fields flattened',
    domain: 'world-building',
  },

  worldSpeciesFieldDefinitions: {
    key: 'worldSpeciesFieldDefinitions',
    label: 'World Species Field Definitions',
    schema: z.object({ worldSpeciesFieldDefinitions: z.array(z.any()).optional() }),
    description: 'Field definitions for world species, used for prompt injection',
    domain: 'world-building',
  },

  // =============================================================================
  // WORLD SYSTEMS (V3)
  // =============================================================================

  worldSystems: {
    key: 'worldSystems',
    label: 'World Systems (V3)',
    schema: z.object({ worldSystems: z.array(z.any()).optional() }),
    description: 'V3 world systems (magic systems, natural laws, social customs, technologies, materials) with latest snapshot resolved',
    domain: 'world-building',
  },

  worldSystemsCurrent: {
    key: 'worldSystemsCurrent',
    label: 'World Systems Current (V3)',
    schema: z.object({ worldSystemsCurrent: z.array(z.any()).optional() }),
    description: 'V3 world systems with latest snapshot fields flattened to top level',
    domain: 'world-building',
  },

  activeWorldSystem: {
    key: 'activeWorldSystem',
    label: 'Active World System (V3)',
    schema: z.object({ activeWorldSystem: z.any().optional() }),
    description: 'The currently active V3 world system with latest snapshot resolved',
    domain: 'world-building',
  },

  activeWorldSystemCurrent: {
    key: 'activeWorldSystemCurrent',
    label: 'Active World System Current (V3)',
    schema: z.object({ activeWorldSystemCurrent: z.any().optional() }),
    description: 'The currently active V3 world system with latest snapshot fields flattened',
    domain: 'world-building',
  },

  worldSystemFieldDefinitions: {
    key: 'worldSystemFieldDefinitions',
    label: 'World System Field Definitions',
    schema: z.object({ worldSystemFieldDefinitions: z.array(z.any()).optional() }),
    description: 'Field definitions for world systems, used for prompt injection',
    domain: 'world-building',
  },

  chapters: {
    key: 'chapters',
    label: 'Chapters (Context)',
    schema: z.object({ chapters: z.array(z.any()).optional() }),
    description: 'Chapter context with content/summaries for AI reference',
    domain: 'chapter-workspace',
  },

  chapterMetadata: {
    key: 'chapterMetadata',
    label: 'Chapters (Metadata)',
    schema: z.object({ chapterMetadata: z.array(z.any()).optional() }),
    description: 'Chapter context without content/summaries for AI reference',
    domain: 'chapter-workspace',
  },

  previousChapters: {
    key: 'previousChapters',
    label: 'Previous Chapters',
    schema: z.object({ previousChapters: z.array(z.any()).optional() }),
    description: 'Previously written chapters for narrative continuity',
    domain: 'chapter-workspace',
  },

  previousChapterMetadata: {
    key: 'previousChapterMetadata',
    label: 'Metadata from Previous Chapters',
    schema: z.object({ previousChapterMetadata: z.array(z.any()).optional() }),
    description: 'The general outline of previous chapters',
    domain: 'chapter-workspace',
  },

  scenes: {
    schema: z.object({ scenes: z.array(z.any()).describe("An array of scenes that make up this chapter.") }),
    description: 'An array of scenes that make up this chapter.',
    domain: 'chapter-workspace',
  },

  // =============================================================================
  // STORY STRUCTURE ELEMENTS
  // =============================================================================

  blurb: {
    schema: z.object({ blurb: z.string().describe("The core idea or summary of your story.") }),
    description: 'The core idea or summary of your story.',
    domain: 'story-planning',
  },
  genre: {
    schema: z.object({ genre: z.string().describe("A list of genres associated with the story (e.g., Fantasy, Sci-Fi).") }),
    description: 'A list of genres associated with the story (e.g., Fantasy, Sci-Fi).',
    domain: 'story-planning',
  },
  language: {
    schema: z.object({ language: z.string().optional().describe("The language of the story (e.g., English, Spanish).") }),
    description: 'The language of the story (e.g., English, Spanish).',
    domain: 'story-planning',
  },
  storyType: {
    schema: z.object({ storyType: z.string().optional().describe("The type of story (e.g., Novel, Novella).") }),
    description: 'The type of story (e.g., Novel, Novella).',
    domain: 'story-planning',
  },
  plotType: {
    schema: z.object({ plotType: z.string().optional().describe("The selected plot type (e.g., The Quest).") }),
    description: 'The selected plot type (e.g., The Quest).',
    domain: 'story-planning',
  },
  pov: {
    schema: z.object({ pov: z.string().optional().describe("The Point of View (e.g., First Person Single).") }),
    description: 'The Point of View (e.g., First Person Single).',
    domain: 'story-planning',
  },
  tense: {
    schema: z.object({ tense: z.string().optional().describe("The narrative tense (e.g., Past, Present).") }),
    description: 'The narrative tense (e.g., Past, Present).',
    domain: 'story-planning',
  },
  projectedWordCount: {
    schema: z.object({ projectedWordCount: z.number().optional().describe("The projected word count for the entire project.") }),
    description: 'The projected word count for the entire project.',
    domain: 'story-planning',
  },
  projectedTotalChapters: {
    schema: z.object({ projectedTotalChapters: z.number().optional().describe("The projected total number of chapters for the story.") }),
    description: 'The projected total number of chapters for the story.',
    domain: 'story-planning',
  },
  universe: {
    schema: z.object({ universe: z.string().optional().describe("The well-known literary or cinematic universe this story is inspired by (e.g., 'Dune', 'Star Wars').") }),
    description: 'The well-known literary or cinematic universe this story is inspired by.',
    domain: 'story-planning',
  },
  storySetting: {
    schema: z.object({ storySetting: z.string().optional().describe("A generic real-world setting for context (e.g., 'Medieval Europe').") }),
    description: 'A generic real-world setting for context.',
    domain: 'story-planning',
  },
  lore: {
    schema: z.object({ lore: z.string().optional().describe("Broader context or established lore for the world.") }),
    description: 'Broader context or established lore for the world.',
    domain: 'story-planning',
  },
  seriesContext: {
    schema: z.object({ seriesContext: z.string().optional().describe("Context from prior books in the series to maintain continuity.") }),
    description: "Context from prior books in the series to maintain continuity.",
    domain: 'story-planning',
  },

  // =============================================================================
  // COMPLEX STORY STRUCTURE ELEMENTS
  // =============================================================================

  summary: {
    schema: z.object({ summary: z.string().describe("An enriched plot summary that weaves together the core theme, central conflict, and a concise overview of the story.") }),
    description: 'An enriched plot summary that weaves together the core theme, central conflict, and a concise overview of the story.',
    domain: 'story-planning',
  },

  writtenPlot: {
    schema: z.object({ writtenPlot: z.string().describe("The detailed, multi-paragraph plot outline in Markdown format. Uses ## headings for 7 structural phases (Initial State through Final State). Present tense, third person. This is the source of truth for the narrative.") }),
    description: 'The detailed, multi-paragraph plot outline in Markdown format. Uses ## headings for 7 structural phases. This is the source of truth for the narrative.',
    domain: 'story-planning',
  },

  completePlot: {
    schema: z.object({ completePlot: z.any().describe("The complete plot context including summary and writtenPlot narrative.") }),
    description: 'The complete plot context including summary and writtenPlot narrative.',
    domain: 'story-planning',
  },

  storyContext: {
    key: 'storyContext',
    label: 'Story Context',
    schema: z.object({ storyContext: z.any() }),
    description: 'Complete story metadata including detailed plot, POV, genre, ...',
    domain: 'story-planning',
  },

  loreContext: {
    key: 'loreContext',
    label: 'Lore Context',
    schema: z.object({ loreContext: z.any() }),
    description: 'Existing lore, incl. universe and setting information',
    domain: 'story-planning',
  },

  latestKnownPlotState: {
    key: 'latestKnownPlotState',
    label: 'Latest Known Plot State',
    schema: z.object({ latestKnownPlotState: z.any().optional() }),
    description: 'The latest cumulative plot snapshot, including plot summary, resolved threads, and unresolved threads. When an active chapter is set, scoped to the snapshot from the chapter immediately before it; otherwise the latest snapshot overall.',
    domain: 'story-planning',
  },

  activeContinuityFacts: {
    key: 'activeContinuityFacts',
    label: 'Active Continuity Facts',
    schema: z.object({ activeContinuityFacts: z.array(z.any()).optional() }),
    description:
      'Story-level binding facts (deadlines, counts, locations, in-world dates, promises, ' +
      'world rules, ongoing conditions) that are still in force as of the active chapter. ' +
      'Resolved facts are excluded.',
    domain: 'story-planning',
  },

  recentlyResolvedContinuityFacts: {
    key: 'recentlyResolvedContinuityFacts',
    label: 'Recently Resolved Continuity Facts',
    schema: z.object({ recentlyResolvedContinuityFacts: z.array(z.any()).optional() }),
    description:
      'Continuity facts resolved in the most recent two chapters before the active chapter. ' +
      'Useful for reminding the writer that a deadline just lapsed or a promise was just kept.',
    domain: 'story-planning',
  },

  existingStorylines: {
    key: 'existingStorylines',
    label: 'Existing Storylines',
    schema: z.object({ existingStorylines: z.any() }),
    description: 'Current storylines with beats and story questions from story planning.',
    domain: 'story-planning',
  },

  chapterDirectory: {
    key: 'chapterDirectory',
    label: 'Chapter Directory',
    schema: z.object({ chapterDirectory: z.array(z.any()).optional() }),
    description: 'Lightweight chapter list (number, title, keyEvents) for cross-referencing plot threads to chapters.',
    domain: 'chapter-workspace',
  },

  existingThreads: {
    key: 'existingThreads',
    label: 'Existing Plot Threads',
    schema: z.object({ existingThreads: z.array(z.any()).optional() }),
    description: 'Active plot threads for thread matching and lifecycle analysis.',
    domain: 'chapter-workspace',
  },

  characterArcs: {
    key: 'characterArcs',
    label: 'Character Arcs',
    schema: z.object({ characterArcs: z.array(z.any()).optional() }),
    description: 'Character arcs with planned beats for arc-touch detection.',
    domain: 'characters',
  },

  chapterSummaries: {
    key: 'chapterSummaries',
    label: 'Chapter Summaries',
    schema: z.object({ chapterSummaries: z.any() }),
    description: 'Aggregated chapter metadata with key events, goals, and plot snapshot summaries for chapters with written content.',
    domain: 'chapter-workspace',
  },

  plannedPlot: {
    key: 'plannedPlot',
    label: 'Planned Plot',
    schema: z.object({ plannedPlot: z.any() }),
    description: 'The author\'s planned plot combining writtenPlot and enriched summary.',
    domain: 'story-planning',
  },

  actualPlotProgression: {
    key: 'actualPlotProgression',
    label: 'Actual Plot Progression',
    schema: z.object({ actualPlotProgression: z.any() }),
    description: 'Per-chapter plot progression from plot snapshots showing what was actually written.',
    domain: 'story-planning',
  },

  storylineDivergenceData: {
    key: 'storylineDivergenceData',
    label: 'Storyline Divergence Data',
    schema: z.object({ storylineDivergenceData: z.any() }),
    description: 'Pre-computed per-storyline comparison: planned beats, coverage, trajectory, missing phases.',
    domain: 'story-planning',
  },

  characterDivergenceData: {
    key: 'characterDivergenceData',
    label: 'Character Divergence Data',
    schema: z.object({ characterDivergenceData: z.any() }),
    description: 'Pre-computed per-character comparison: planned arc beats vs latest snapshot state.',
    domain: 'story-planning',
  },

  plotThreadStatus: {
    key: 'plotThreadStatus',
    label: 'Plot Thread Status',
    schema: z.object({ plotThreadStatus: z.any() }),
    description: 'All plot threads with title, question, status, and chapter lifecycle.',
    domain: 'chapter-workspace',
  },

  openPlotThreads: {
    key: 'openPlotThreads',
    label: 'Open Plot Threads',
    schema: z.object({ openPlotThreads: z.any() }),
    description: 'Only active (unresolved) plot threads — the dramatic questions a drafter must continue or resolve.',
    domain: 'chapter-workspace',
  },

  isShortForm: {
    key: 'isShortForm',
    label: 'Short-Form Project',
    schema: z.object({ isShortForm: z.boolean().optional() }),
    description: 'Whether the project is short-form (single-chapter story vs multi-chapter narrative).',
    domain: 'story-planning',
  },

  shortFormDraftContext: {
    key: 'shortFormDraftContext',
    label: 'Short-Form Draft Context',
    schema: z.object({ shortFormDraftContext: z.any() }),
    description: 'Single-chapter draft context for short stories: key events, chapter goals, plot snapshot summary, and content excerpt.',
    domain: 'chapter-workspace',
  },

  divergenceReport: {
    key: 'divergenceReport',
    label: 'Divergence Report',
    schema: z.object({ divergenceReport: z.any() }),
    description: 'Output from divergence-report-v2: summary, plot/storyline/character/thread divergences with beat-level detail.',
    domain: 'story-planning',
  },

  initialChapterContent: {
    key: 'initialChapterContent',
    label: 'Initial Chapter Content',
    schema: z.object({ initialChapterContent: z.string().optional() }),
    description: 'The content of the first few chapters of the story.',
    domain: 'chapter-workspace',
  },

  activeChapterContent: {
    key: 'activeChapterContent',
    label: 'Active Chapter Content',
    schema: z.object({ activeChapterContent: z.string().optional() }),
    description: 'The content of the currently active chapter, if available.',
    domain: 'chapter-workspace',
  },

  draftingHistory: {
    key: 'draftingHistory',
    label: 'Drafting History',
    schema: z.object({ draftingHistory: z.any().optional() }),
    description: 'Complete drafting session history including all agent outputs, content versions, and evaluations.',
    domain: 'chapter-workspace',
  },

  acceptedFindings: {
    key: 'acceptedFindings',
    label: 'Accepted Findings',
    schema: z.object({ acceptedFindings: z.array(z.any()).optional() }),
    description: 'Findings from prior critique runs that the author addressed via refine. Signals which advice the author values.',
    domain: 'chapter-workspace',
  },

  rejectedFindings: {
    key: 'rejectedFindings',
    label: 'Rejected Findings',
    schema: z.object({ rejectedFindings: z.array(z.any()).optional() }),
    description: 'Findings from prior critique runs that the author explicitly dismissed. Signals which advice the author does not want repeated.',
    domain: 'chapter-workspace',
  },

  acceptedWeaknesses: {
    key: 'acceptedWeaknesses',
    label: 'Accepted Weaknesses',
    schema: z.object({ acceptedWeaknesses: z.array(z.any()).optional() }),
    description: 'Systemic weaknesses from prior critique runs that the author addressed via refine. Signals which broad notes the author values.',
    domain: 'chapter-workspace',
  },

  rejectedWeaknesses: {
    key: 'rejectedWeaknesses',
    label: 'Rejected Weaknesses',
    schema: z.object({ rejectedWeaknesses: z.array(z.any()).optional() }),
    description: 'Systemic weaknesses from prior critique runs that the author explicitly dismissed. Signals which broad notes the author rejects.',
    domain: 'chapter-workspace',
  },

  currentStrengths: {
    key: 'currentStrengths',
    label: 'Current Strengths',
    schema: z.object({ currentStrengths: z.array(z.any()).optional() }),
    description: 'Surviving strengths the author has kept on the chapter. Critics should avoid suggestions that would undermine these.',
    domain: 'chapter-workspace',
  },

  lastThreeChaptersContent: {
    key: 'lastThreeChaptersContent',
    label: 'Last 3 Chapters Content',
    schema: z.object({ lastThreeChaptersContent: z.string().optional() }),
    description: 'The content of the three chapters preceding the active one.',
    domain: 'chapter-workspace',
  },

  fullStoryContent: {
    key: 'fullStoryContent',
    label: 'Full Story Content',
    schema: z.object({ fullStoryContent: z.string().optional() }),
    description: 'The full prose of every written chapter, concatenated in order with chapter titles as headings. Used by agents that need the complete written story.',
    domain: 'chapter-workspace',
  },

   actualLastThreeChaptersContent: {
    key: 'actualLastThreeChaptersContent',
    label: 'Actual Last 3 Chapters Content',
    schema: z.object({ actualLastThreeChaptersContent: z.string().optional() }),
    description: 'The content of the actual last three written chapters of the story.',
    domain: 'chapter-workspace',
  },

  previousChapterTail: {
    key: 'previousChapterTail',
    label: 'Previous Chapter Tail',
    schema: z.object({ previousChapterTail: z.string().optional() }),
    description: 'The final ~1000 words of the chapter immediately preceding the active one. Useful for picking up directly where the prior chapter left off.',
    domain: 'chapter-workspace',
  },

  // =============================================================================
  // CHAPTER ATTRIBUTE ACCESSORS
  // =============================================================================

  lastChapterNumber: {
    schema: z.object({ lastChapterNumber: z.number().describe('The number of the last existing chapter.') }),
    description: 'The number of the last existing chapter.',
    domain: 'chapter-workspace',
  },
  nextChapterNumber: {
    schema: z.object({ nextChapterNumber: z.number().describe('The next available chapter number.') }),
    description: 'The next available chapter number.',
    domain: 'chapter-workspace',
  },
  chapterNumber: {
    schema: z.object({ chapterNumber: z.number().describe('The number of the current chapter') }),
    description: 'The number of the current chapter',
    domain: 'chapter-workspace',
  },
  chapterContent: {
    schema: z.object({ chapterContent: z.string().describe("The full HTML content of the chapter to analyze.") }),
    description: 'The full HTML content of the chapter to analyze.',
    domain: 'chapter-workspace',
  },
  chapterTitle: {
    schema: z.object({ chapterTitle: z.string().describe("The title of the current chapter.") }),
    description: 'The title of the current chapter.',
    domain: 'chapter-workspace',
  },
  chapterKeyEvents: {
    schema: z.object({ chapterKeyEvents: z.string().describe("The key events for the current chapter.") }),
    description: 'The key events for the current chapter.',
    domain: 'chapter-workspace',
  },
  chapterGoals: {
    schema: z.object({ chapterGoals: z.object({
        plotDevelopment: z.string().describe("How this chapter moves the plot forward. 1-2 sentences."),
        charDevelopment: z.string().describe("How this chapter develops characters and relationships. 1-2 sentences."),
        stakesAndTension: z.string().describe("How this chapter escalates tension or raises the stakes. 1-2 sentences.")
    }).optional().describe("The narrative goals for the chapter.") }),
    description: 'The narrative goals for the chapter (plot, character, stakes).',
    domain: 'chapter-workspace',
  },
  chapterPacing: {
    schema: z.object({ chapterPacing: z.string().describe("The pacing for the current chapter.") }),
    description: 'The pacing for the current chapter.',
    domain: 'chapter-workspace',
  },
  chapterPovCharacter: {
    schema: z.object({ chapterPovCharacter: z.string().nullable().optional().describe("The name of the character whose narrative perspective is used for THIS chapter.") }),
    description: 'The name of the character whose narrative perspective is used for THIS chapter.',
    domain: 'chapter-workspace',
  },
  chapterPovJustification: {
    schema: z.object({ chapterPovJustification: z.string().optional().describe("An explanation of why a character is the narrator for THIS chapter.") }),
    description: 'An explanation of why a character is the narrator for THIS chapter.',
    domain: 'chapter-workspace',
  },
  chapterProjectedWordCount: {
    schema: z.object({ chapterProjectedWordCount: z.number().optional().describe("The projected word count for the current chapter.") }),
    description: 'The projected word count for the current chapter.',
    domain: 'chapter-workspace',
  },
  agentTargetWordCount: {
    schema: z.object({ agentTargetWordCount: z.number().optional().describe('The word count given to the agent for the current chapter.') }),
    description: 'The word count given to the agent for the current chapter.',
    domain: 'chapter-workspace',
  },

  // =============================================================================
  // INDIVIDUAL, COMPLETE CHAPTER
  // =============================================================================

  activeChapterComplete: {
    schema: z.object({ activeChapter: z.any().describe("The full chapter object that is currently being reviewed or edited.") }),
    description: "The full chapter object that is currently being reviewed or edited.",
    domain: 'chapter-workspace',
  },

  chapterContext: {
    schema: z.object({ chapterContext: z.any().describe("Comprehensive context on the current chapter.") }),
    description: "Comprehensive context on the current chapter.",
    domain: 'chapter-workspace',
  },

  nextChapterContext: {
    schema: z.object({ nextChapterContext: z.any().optional().describe("Context on the next chapter (if it exists), used to define chapter boundaries.") }),
    description: "Context on the next chapter (if it exists), used to define chapter boundaries.",
    domain: 'chapter-workspace',
  },

  previousChapterContext: {
    schema: z.object({ previousChapterContext: z.any().optional().describe("Context on the previous chapter (if it exists), used to define chapter boundaries.") }),
    description: "Context on the previous chapter (if it exists), used to define chapter boundaries.",
    domain: 'chapter-workspace',
  },

  // =============================================================================
  // INDIVIDUAL CHARACTER ELEMENTS
  // =============================================================================

  characterName: {
    schema: z.object({ characterName: z.string().describe("The character's name.") }),
    description: "The character's name.",
    domain: 'characters',
  },
  
  gender: {
    schema: z.object({ gender: z.string().optional().describe("The character's gender.") }),
    description: "The character's gender.",
    domain: 'characters',
  },
  height: {
    schema: z.object({ height: z.string().optional().describe("The character's height.") }),
    description: "The character's height.",
    domain: 'characters',
  },
  species: {
    schema: z.object({ species: z.string().optional().describe("The character's species.") }),
    description: "The character's species.",
    domain: 'characters',
  },

  existingDetails: {
    schema: z.object({ existingDetails: z.record(z.string()).describe("A key-value object of details that are already filled out for the character.") }),
    description: 'A key-value object of details that are already filled out for the character.',
    domain: 'characters',
  },

  existingDetailsCurrent: {
    schema: z.object({ existingDetailsCurrent: z.record(z.string()).describe("A key-value object of details that are already filled out for the character.") }),
    description: 'A key-value object of details that are already filled out for the character.',
    domain: 'characters',
  },

  existingDetailsForChapter: {
    schema: z.object({ existingDetailsForChapter: z.record(z.string()).describe("A key-value object of character details from a specific chapter snapshot, falling back to the latest or initial snapshot.") }),
    description: 'Character details from a specific chapter snapshot (uses activeChapterId), with fallback to latest/initial snapshot.',
    domain: 'characters',
  },

  chosenInspirations: {
    key: 'chosenInspirations',
    label: 'Chosen Inspirations',
    schema: z.object({ chosenInspirations: z.array(z.string()).optional() }),
    description: 'The names of the real-world personalities chosen as inspiration for the active character.',
    domain: 'characters',
  },

  inspirationFacialDescriptions: {
    key: 'inspirationFacialDescriptions',
    label: 'Inspiration Facial Descriptions',
    schema: z.object({ inspirationFacialDescriptions: z.object({
      primary: z.object({
        name: z.string(),
        description: z.string(),
      }).optional(),
      secondary: z.array(z.object({
        name: z.string(),
        description: z.string(),
      })).optional(),
    }).optional() }),
    description: 'Detailed facial descriptions extracted from chosen inspirations for portrait generation.',
    domain: 'characters',
  },

  inspirationFullDescriptions: {
    key: 'inspirationFullDescriptions',
    label: 'Inspiration Full Descriptions',
    schema: z.object({ inspirationFullDescriptions: z.object({
      primary: z.object({
        name: z.string(),
        description: z.string(),
      }).optional(),
      secondary: z.array(z.object({
        name: z.string(),
        description: z.string(),
      })).optional(),
    }).optional() }),
    description: 'Complete physical descriptions extracted from chosen inspirations for character image generation.',
    domain: 'characters',
  },

  // =============================================================================
  // INDIVIDUAL, COMPLETE CHARACTER
  // =============================================================================

    activeCharacterContext: {
        schema: z.object({ activeCharacterContext: z.any().describe("The full character object that is currently being reviewed or edited.") }),
        description: "The full character object that is currently being reviewed or edited.",
        domain: 'characters',
    },

    activeCharacterCurrentContext: {
      schema: z.object({ activeCharacterCurrentContext: z.any().describe("The full character object that is currently being reviewed or edited.") }),
      description: "The full character object that is currently being reviewed or edited.",
      domain: 'characters',
    },
    activeCharacterJourney: {
      schema: z.object({ activeCharacterJourney: z.any().describe("The coplete character journey across the scope of the story.") }),
      description: "The coplete character journey across the scope of the story.",
      domain: 'characters',
    },
    povCharacter: {
        schema: z.object({ povCharacter: z.any().nullable().optional().describe("The full character object for this chapter's narrator.") }),
        description: "The full character object for this chapter's narrator.",
        domain: 'characters',
    },

  // =============================================================================
  // STYLE GUIDE ELEMENTS
  // =============================================================================

    styleGuide: {
        schema: z.object({ styleGuide: z.string().optional().describe("The content of the author's active style guide.") }),
        description: "The content of the author's active style guide.",
        domain: 'style-guides',
    },
    bannedNames: {
        schema: z.object({ bannedNames: z.array(z.string()).optional().describe("A list of character names that should be avoided when creating new characters.") }),
        description: 'A list of character names that should be avoided when creating new characters.',
        domain: 'style-guides',
    },
    genreTemplates: {
        schema: z.object({ genreTemplates: z.array(z.any()).describe("An array of prose objects for each selected genre influence.") }),
        description: 'An array of prose objects for each selected genre influence.',
        domain: 'style-guides',
    },
    writerTemplates: {
        schema: z.object({ writerTemplates: z.array(z.any()).describe("An array of prose objects for each selected writer influence.") }),
        description: 'An array of prose objects for each selected writer influence.',
        domain: 'style-guides',
    },
    universeTemplates: {
        schema: z.object({ universeTemplates: z.array(z.any()).describe("An array of prose objects for each selected universe influence.") }),
        description: 'An array of prose objects for each selected universe influence.',
        domain: 'style-guides',
    },
    writingStyleSample: {
        schema: z.object({ writingStyleSample: z.string().optional().describe("Concatenated text of every named writing sample on the project, used as a voice/style reference. Truncated to 150k characters.") }),
        description: "Concatenated text of every named writing sample on the project, used as a voice/style reference.",
        domain: 'writing-samples',
    },
    writingBlocklist: {
        key: 'writingBlocklist',
        label: 'Writing Blocklist',
        schema: z.object({ writingBlocklist: z.array(z.string()).optional().describe("Names and terms that must never appear in generated output.") }),
        description: "Names and terms that must never appear in generated output.",
        domain: 'style-guides',
    },
    writingStyleSamples: {
        schema: z.object({
          writingStyleSamples: z
            .array(
              z.object({
                id: z.string(),
                name: z.string(),
                text: z.string(),
              })
            )
            .optional()
            .describe(
              "The author's named writing samples (from project.writingSamples), used as a corpus of voice/style references."
            ),
        }),
        description: "Named writing samples from the author, used as a voice/style corpus for stylization.",
        domain: 'style-guides',
    },
    sliderDefinitions: {
      schema: z.object({ sliderDefinitions: z.any() }),
      description: 'The full definitions for all 14 style guide sliders, used as a rubric for analysis.',
      domain: 'style-guides',
    },
    selectedSliderLevels: {
      schema: z.object({ selectedSliderLevels: z.any() }),
      description: 'The detailed definition for the user-selected level of each style guide slider.',
      domain: 'style-guides',
    },

  // =============================================================================
  // VARIABLES THAT WILL BE FILLED AT RUNTIME
  // =============================================================================

    userMessage: {
      schema: z.object({ userMessage: z.string().optional().describe("An optional user-provided message to guide the generation.") }),
      description: 'An optional user-provided message to guide the generation.',
      domain: 'runtime',
    },
    
    imageStyle: {
        schema: z.object({ imageStyle: z.string().optional().describe("The selected artistic style for image generation.") }),
        description: 'The selected artistic style for image generation.',
        domain: 'runtime',
    },
    
    shotType: {
        schema: z.object({ shotType: z.string().optional().describe("The selected camera shot type for image generation.") }),
        description: 'The selected camera shot type for image generation.',
        domain: 'runtime',
    },

    outfitDescription: {
        schema: z.object({ outfitDescription: z.string().optional().describe("Structured description of the outfit the character should be rendered wearing, overriding the character's inherent clothing attributes.") }),
        description: "Structured description of the outfit the character should be rendered wearing. When present, the image-prompt agent must disregard the character's own clothing details and use this outfit instead.",
        domain: 'runtime',
    },

    imagePrompt: {
      schema: z.object({ imagePrompt: z.string().describe("The detailed, artistic prompt for the image generation model.") }),
      description: "The detailed, artistic prompt for the image generation model.",
      domain: 'runtime',
  },
    existingStory: {
        schema: z.object({ existingStory: z.string().describe("The story you want to continue from.") }),
        description: 'Input to the Sequel Plot Generation. The story you want to continue from.',
        domain: 'runtime',
    },
    existingStories: {
        schema: z.object({ existingStories: z.array(z.string()).describe("A list of stories to use as inspiration.") }),
        description: 'Input to the Inspired Plot Generation. A list of stories to use as inspiration.',
        domain: 'runtime',
    },
    
    textInput: {
        schema: z.object({ textInput: z.string().describe("Unstructured text that will be processed by the agent.") }),
        description: 'Unstructured text that will be processed by the agent.',
        domain: 'runtime',
    },

    existingProse: {
        schema: z.object({ existingProse: z.string().describe("The existing story content from the editor.") }),
        description: 'The existing story content currently in the editor.',
        domain: 'runtime',
    },

    selectedText: {
        schema: z.object({ selectedText: z.string().describe("The specific text selection to revise.") }),
        description: 'The specific text selection to revise.',
        domain: 'runtime',
    },    

    originalPrompt: {
        schema: z.object({ originalPrompt: z.string().optional().describe("The prompt used to generate the options.") }),
        description: 'The prompt used to generate the options.',
        domain: 'runtime',
    },
    evalOptions: {
        schema: z.object({ options: z.array(z.any()).describe("The list of generated text options to evaluate.") }),
        description: 'The list of generated text options to evaluate.',
        domain: 'runtime',
    },
    genreGuidance: {
        schema: z.object({ genreGuidance: z.string().optional().describe("Markdown block of per-genre prose-evaluation guidance, computed by the caller from the project's genres.") }),
        description: "Per-genre prose-evaluation guidance, computed by the caller from the project's genres.",
        domain: 'runtime',
    },
    continuityGenreGuidance: {
        schema: z.object({ continuityGenreGuidance: z.string().optional().describe("Markdown block of per-genre continuity-evaluation guidance, computed by the caller from the project's genres.") }),
        description: "Per-genre continuity-evaluation guidance, computed by the caller from the project's genres.",
        domain: 'runtime',
    },
    draftingGenreGuidance: {
        schema: z.object({ draftingGenreGuidance: z.string().optional().describe("Markdown block of per-genre drafting guidance — what to aim for, pacing cues, and pitfalls — derived from the project's genres.") }),
        description: "Per-genre drafting guidance derived from the project's genres. Surfaces what to aim for, pacing cues, and common first-draft pitfalls so drafters write closer to genre conventions out of the gate.",
        domain: 'runtime',
    },
    planningGenreGuidance: {
        schema: z.object({ planningGenreGuidance: z.string().optional().describe("Markdown block of per-genre planning guidance — reader contract, plot/beat shape, and outline pitfalls — derived from the project's genres.") }),
        description: "Per-genre planning guidance derived from the project's genres. Surfaces the genre's reader contract, the expected shape of beats / escalation / climax, and outline-stage pitfalls. Texture / prose-craft advice is intentionally omitted (handled by drafters).",
        domain: 'runtime',
    },

    journeyFields: {
      schema: z.object({ options: z.array(z.any()).describe("The available journey information for a character.") }),
      description: "The available journey information for a character.",
      domain: 'runtime',
  },

  // =============================================================================
  // ISSUE CLASSIFICATION
  // =============================================================================

  issueClassificationRubric: {
    schema: z.object({ issueClassificationRubric: z.any() }),
    description: 'The complete issue classification rubric including severity levels, confidence levels, domains, and scope definitions.',
    domain: 'evaluation',
  },

} as const;
